<template>
    <VJoyDialog ref="dialogRef" :cancel-text="$t('customer.freelancer.pof.modal.create.list.button.cancel')" @dialog:hide="onDialogHidden()">
        <template #dialog-header>
            {{ $t('customer.freelancer.pof.modal.create.list.title.v2') }}
        </template>

        <template #dialog-subheader>
            {{ $t('customer.freelancer.pof.modal.create.list.subtitle.collaboration') }}
        </template>

        <template #dialog-body>
            <FreelancerListForm ref="form" />
        </template>

        <template #dialog-confirm>
            <VJoyButton :disabled="isCreationDisabled" variant="main" data-testid="create-freelancer-list-confirm" @click="submitForm">
                {{ $t('customer.freelancer.pof.modal.create.list.button.create') }}
            </VJoyButton>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref, onMounted, onBeforeUnmount} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyButton, VJoyDialog} from '@maltjoy/core-vue';
    import {createSnackNotifier} from '~/composables';
    import {eventBus} from '~/config/events';
    import FreelancerListForm from './FreelancerListForm.vue';
    import {usePoolOfFreelancesStore} from '../../stores/pool-of-freelances.store';
    import type {FreelancerProfile} from '../types';

    const {t} = useTranslation();
    const notifier = createSnackNotifier();
    const poolOfFreelancesStore = usePoolOfFreelancesStore();

    const dialogRef = ref<InstanceType<typeof VJoyDialog> | null>(null);
    const form = ref<InstanceType<typeof FreelancerListForm>>();
    const savingForm = ref(false);
    const listCreated = ref(false);
    const profile = ref<FreelancerProfile>();

    const isCreationDisabled = computed(() => savingForm.value);

    async function submitForm() {
        if (!profile.value) {
            return;
        }
        if (!form.value || (await form.value.isFormInvalid())) {
            return;
        }
        savingForm.value = true;

        try {
            const {freelancerListName, sharedWithinCompany} = form.value.getValues();
            const newFreelancerList = await poolOfFreelancesStore.createNewFreelancersList(freelancerListName, sharedWithinCompany, profile.value.id);
            if (newFreelancerList) {
                const newFreelancerListDetailUrl = `/client/freelancers/list/${newFreelancerList.id}`;
                notifier.push(
                    'success',
                    t('customer.freelancer.pof.modal.create.list.notif.success.v2', [freelancerListName, newFreelancerListDetailUrl]),
                );
                form.value.resetForm();
                await poolOfFreelancesStore.fetchFreelancersLists();
                listCreated.value = true;
                await dialogRef.value?.hide();
            }
        } finally {
            savingForm.value = false;
        }
    }

    async function openDialog(profileToAddToCreatedList: FreelancerProfile) {
        profile.value = profileToAddToCreatedList;
        await dialogRef.value?.show();
        poolOfFreelancesStore.fetchFreelancersLists();
    }

    function onDialogHidden() {
        if (form.value) {
            form.value.resetForm();
        }
        if (!listCreated.value && profile.value) {
            eventBus.emit('profile-card:favorite:add-profile:open-dialog', profile.value);
        }
    }

    onMounted(() => {
        eventBus.on('profile-card:favorite:create-list:open-dialog', openDialog);
    });

    onBeforeUnmount(() => {
        eventBus.off('profile-card:favorite:create-list:open-dialog');
    });
</script>
